import React from 'react';

/**
 * Generic identity/category badge for one-off badge types.
 *
 * Use this for badges where the color represents a category or identity,
 * not a workflow status (use StatusBadge for that) or employment classification
 * (use ClassificationBadge for that).
 *
 * Examples: relationship type, address type, qualification level, employment type,
 * country, document category, license class, etc.
 *
 * @param {string} color - Named color: 'blue','green','red','orange','purple','pink',
 *                         'amber','cyan','teal','indigo','violet','slate','gray','rose',
 *                         'emerald','fuchsia','sky','lime'
 * @param {string} label - Display text
 * @param {'xs'|'md'} [size] - 'xs' for compact inline contexts, 'md' for prominent contexts
 * @param {string} [className] - Extra classes (margins, flex-shrink, etc.)
 */

const COLOR_MAP = {
  blue:    'bg-blue-50 text-blue-700 ring-blue-600/20',
  green:   'bg-green-50 text-green-700 ring-green-600/20',
  red:     'bg-red-50 text-red-700 ring-red-600/20',
  orange:  'bg-orange-50 text-orange-700 ring-orange-600/20',
  purple:  'bg-purple-50 text-purple-700 ring-purple-600/20',
  pink:    'bg-pink-50 text-pink-700 ring-pink-600/20',
  amber:   'bg-amber-50 text-amber-700 ring-amber-600/20',
  cyan:    'bg-cyan-50 text-cyan-700 ring-cyan-600/20',
  teal:    'bg-teal-50 text-teal-700 ring-teal-600/20',
  indigo:  'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
  violet:  'bg-violet-50 text-violet-700 ring-violet-600/20',
  slate:   'bg-slate-50 text-slate-700 ring-slate-600/20',
  gray:    'bg-gray-100 text-gray-700 ring-gray-600/20',
  rose:    'bg-rose-50 text-rose-700 ring-rose-600/20',
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  fuchsia: 'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-600/20',
  sky:     'bg-sky-50 text-sky-700 ring-sky-600/20',
  lime:    'bg-lime-50 text-lime-700 ring-lime-600/20',
};

export const InfoBadge = ({ color = 'gray', label, size, className = '', title }) => {
  const colorClasses = COLOR_MAP[color] || COLOR_MAP.gray;
  const sizeClasses =
    size === 'md' ? 'px-3 py-1 text-xs'
    : size === 'xs' ? 'px-1.5 py-0.5 text-[10px]'
    : 'px-2 py-0.5 text-[11px]';

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ring-1 ring-inset whitespace-nowrap ${colorClasses} ${sizeClasses} ${className}`}
      title={title ?? (typeof label === 'string' ? label : undefined)}
    >
      {label}
    </span>
  );
};

export default InfoBadge;
